import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, Link } from 'react-router-dom'
import { EnvelopeSimple, Lock, User, ArrowRight } from '@phosphor-icons/react'
import { toast } from 'sonner'

const bc = 'Barlow Condensed, sans-serif'
const b = 'Barlow, sans-serif'

export default function AuthPage() {
  const [mode, setMode] = useState('login')
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({ email: '', password: '', name: '' })
  const { login, register } = useAuth()
  const navigate = useNavigate()

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }))

  const handleSubmit = async e => {
    e.preventDefault()
    setLoading(true)
    try {
      if (mode === 'login') {
        await login(form.email, form.password)
        toast.success('¡Bienvenido de vuelta!')
      } else {
        if (!form.name.trim()) {
          toast.error('El nombre es requerido')
          return
        }
        await register(form.email, form.password, form.name)
        toast.success('¡Cuenta creada!')
      }
      navigate('/')
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar sesión')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex">
      {/* ── Left panel — branding ─────────────────────────────── */}
      <div className="hidden lg:flex flex-col justify-between w-[480px] flex-shrink-0 border-r border-[#1E1E1E] p-12">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 no-underline">
          <div
            className="w-7 h-7 bg-[#D42B1E] flex items-center justify-center flex-shrink-0"
            style={{ borderRadius: 2 }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path
                d="M1.5 3.2L6.5 1L11.5 3.2V6.5C11.5 9.5 6.5 12 6.5 12C6.5 12 1.5 9.5 1.5 6.5V3.2Z"
                fill="white"
                opacity="0.9"
              />
            </svg>
          </div>
          <span
            className="text-[#E8E6E0] text-sm font-bold tracking-widest uppercase"
            style={{ fontFamily: bc }}
          >
            RDK<span className="text-[#D42B1E]">·</span>CC
          </span>
        </Link>

        {/* Quote */}
        <div>
          <p
            className="text-[#E8E6E0] font-black uppercase leading-none mb-6"
            style={{ fontFamily: bc, fontSize: 52, letterSpacing: '-0.02em', lineHeight: 0.93 }}
          >
            Tu voz.
            <br />
            <span className="italic text-[#D42B1E]">Tu guion.</span>
            <br />
            Tu canal.
          </p>
          <p
            className="text-[#555] text-sm leading-relaxed"
            style={{ fontFamily: b, maxWidth: 300 }}
          >
            El estudio creativo con IA para YouTubers que se toman en serio lo que hacen.
          </p>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-3">
          {[
            ['01', 'Configura tu perfil en 3 minutos'],
            ['02', 'Genera guiones en tu voz'],
            ['03', 'Publica con más consistencia'],
          ].map(([n, t]) => (
            <div key={n} className="flex items-center gap-4">
              <span
                className="text-[#282828] font-black text-lg w-6 flex-shrink-0"
                style={{ fontFamily: bc }}
              >
                {n}
              </span>
              <span
                className="text-[#333] text-xs uppercase tracking-widest font-bold"
                style={{ fontFamily: bc }}
              >
                {t}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel — form ────────────────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Mobile logo */}
        <div className="flex items-center gap-3 mb-12 lg:hidden">
          <div
            className="w-7 h-7 bg-[#D42B1E] flex items-center justify-center flex-shrink-0"
            style={{ borderRadius: 2 }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path
                d="M1.5 3.2L6.5 1L11.5 3.2V6.5C11.5 9.5 6.5 12 6.5 12C6.5 12 1.5 9.5 1.5 6.5V3.2Z"
                fill="white"
                opacity="0.9"
              />
            </svg>
          </div>
          <span
            className="text-[#E8E6E0] text-sm font-black tracking-widest uppercase"
            style={{ fontFamily: bc }}
          >
            RDK<span className="text-[#D42B1E]">·</span>CC
          </span>
        </div>

        <div className="w-full max-w-sm">
          {/* Heading */}
          <div className="mb-8">
            <p
              className="text-[#555] text-xs font-bold tracking-[0.18em] uppercase mb-3"
              style={{ fontFamily: bc }}
            >
              {mode === 'login' ? 'Bienvenido de vuelta' : 'Crear cuenta'}
            </p>
            <h1
              className="text-[#E8E6E0] font-black uppercase leading-none"
              style={{ fontFamily: bc, fontSize: 44, letterSpacing: '-0.02em', lineHeight: 0.93 }}
            >
              {mode === 'login' ? (
                <>
                  Entrar
                  <br />
                  <span className="italic text-[#D42B1E]">al estudio.</span>
                </>
              ) : (
                <>
                  Empieza
                  <br />
                  <span className="italic text-[#D42B1E]">gratis.</span>
                </>
              )}
            </h1>
          </div>

          {/* Tab toggle */}
          <div className="flex border border-[#1E1E1E] mb-8" style={{ borderRadius: 2 }}>
            {[
              ['login', 'Iniciar sesión'],
              ['register', 'Crear cuenta'],
            ].map(([m, label]) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 text-xs font-bold tracking-widest uppercase transition-colors ${
                  mode === m
                    ? 'bg-[#D42B1E] text-white'
                    : 'bg-transparent text-[#555] hover:text-[#E8E6E0]'
                }`}
                style={{ fontFamily: bc, borderRadius: 0 }}
                data-testid={`tab-${m}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {mode === 'register' && (
              <Field label="Nombre" icon={<User size={14} />}>
                <input
                  type="text"
                  value={form.name}
                  onChange={set('name')}
                  placeholder="Tu nombre"
                  required
                  data-testid="input-name"
                  className="auth-input"
                  style={{ fontFamily: b }}
                />
              </Field>
            )}

            <Field label="Email" icon={<EnvelopeSimple size={14} />}>
              <input
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="tu@email.com"
                required
                data-testid="input-email"
                className="auth-input"
                style={{ fontFamily: b }}
              />
            </Field>

            <Field label="Contraseña" icon={<Lock size={14} />}>
              <input
                type="password"
                value={form.password}
                onChange={set('password')}
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="input-password"
                className="auth-input"
                style={{ fontFamily: b }}
              />
            </Field>

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-auth-btn"
              className="w-full bg-[#D42B1E] hover:bg-[#b82418] text-white py-3.5 flex items-center justify-center gap-2 font-bold text-sm tracking-widest uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-1"
              style={{ fontFamily: bc, borderRadius: 2 }}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {mode === 'login' ? 'Entrar' : 'Crear cuenta'}
                  <ArrowRight size={15} weight="bold" />
                </>
              )}
            </button>
          </form>

          {/* Switch mode */}
          <p className="text-center text-[#333] text-xs mt-6" style={{ fontFamily: b }}>
            {mode === 'login' ? '¿Sin cuenta? ' : '¿Ya tienes cuenta? '}
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-[#555] hover:text-[#E8E6E0] underline transition-colors"
            >
              {mode === 'login' ? 'Crear una gratis' : 'Iniciar sesión'}
            </button>
          </p>

          {/* Back to landing */}
          <div className="mt-8 pt-8 border-t border-[#1E1E1E] text-center">
            <Link
              to="/"
              className="text-[#2E2E2E] hover:text-[#555] text-xs font-bold tracking-widest uppercase transition-colors no-underline"
              style={{ fontFamily: bc }}
            >
              ← Volver al inicio
            </Link>
          </div>
        </div>
      </div>

      {/* ── Input styles ──────────────────────────────────────── */}
      <style>{`
        .auth-input {
          width: 100%;
          background: #141414;
          border: 1px solid #1E1E1E;
          border-radius: 2px;
          padding: 11px 12px 11px 36px;
          color: #E8E6E0;
          font-size: 13px;
          outline: none;
          transition: border-color 150ms;
        }
        .auth-input::placeholder { color: #2E2E2E; }
        .auth-input:focus { border-color: #D42B1E; }
      `}</style>
    </div>
  )
}

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({ label, icon, children }) {
  return (
    <div>
      <label
        className="block text-[#555] text-[10px] font-bold tracking-[0.18em] uppercase mb-2"
        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
      >
        {label}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#333] pointer-events-none">
          {icon}
        </span>
        {children}
      </div>
    </div>
  )
}
